import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import type { GridApi } from 'ag-grid-community';

import type { Timezone } from '@/datetime/timezone.service';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import type { RangeExport } from '@/trendData/duration.store';
import { TreemapPropsOnly } from '@/treemap/TreemapPropsOnly.molecule';
import type { SerializedRange } from '@/datetime/dateTime.utilities';
import { serializeRange } from '@/datetime/dateTime.utilities';
import type { ColumnOrRowWithDefinitions, TreemapAsset, TreemapViewSettings } from '@/tableBuilder/tableBuilder.types';
import { getTreemapStructure } from '@/tableBuilder/tableBuilderTreemap.utilities';
import type { ConditionItem, ScalarItem, SeriesItem } from '@/trendData/trendData.types';
import type { SelectOption } from '@/formbuilder/SelectFormComponent';
import { useTranslation } from 'react-i18next';

interface TableBuilderTreemapProps {
  conditions: ConditionItem[];
  displayRange: RangeExport;
  handleSwapAssets: (asset: TreemapAsset) => void;
  isPresentationMode: boolean;
  setTreemapViewSettings: (newSettings: TreemapViewSettings) => void;
  signals: (SeriesItem | ScalarItem)[];
  simpleColumns: ColumnOrRowWithDefinitions[];
  timezone: Timezone;
  treemapViewSettings: TreemapViewSettings;
  agGridApi?: GridApi | null;
  proportionBySignal?: string;
  proportionByColumn?: string;
  isUniformSizing: boolean;
  treemapSquareScalingInverse: boolean;
  saveTreemapConfiguration: (
    selectedSignal: any,
    selectedColumn: any,
    treemapSquareScalingInverse: boolean,
    isUniformSizing: boolean,
  ) => void;
}

export const TableBuilderTreemap: FC<TableBuilderTreemapProps> = ({
  agGridApi,
  conditions,
  displayRange,
  handleSwapAssets,
  isPresentationMode,
  setTreemapViewSettings,
  signals,
  simpleColumns,
  timezone,
  treemapViewSettings,
  proportionBySignal,
  proportionByColumn,
  isUniformSizing,
  treemapSquareScalingInverse,
  saveTreemapConfiguration,
}) => {
  const { t } = useTranslation();

  const proportionByValidOptions = [
    'STATISTICS.MAXIMUM.LONG',
    'STATISTICS.MINIMUM.LONG',
    'STATISTICS.AVERAGE.LONG',
    'STATISTICS.PERCENTGOOD.LONG',
    'STATISTICS.COUNT.LONG',
  ];

  const [showModal, setShowModal] = React.useState(false);

  const { helpKey, breadcrumbs, parent, tree } = useMemo(
    () =>
      getTreemapStructure({
        agGridApi,
        conditions,
        simpleColumns,
        signals,
        currentSettings: treemapViewSettings,
        setTreemapViewSettings,
        proportionBySignal,
        proportionByColumn,
        isUniformSizing,
        treemapSquareScalingInverse,
      }),
    [
      agGridApi,
      conditions,
      simpleColumns,
      signals,
      treemapViewSettings,
      setTreemapViewSettings,
      proportionBySignal,
      proportionByColumn,
      isUniformSizing,
      treemapSquareScalingInverse,
    ],
  );

  const handleModalState = (open: boolean) => {
    setShowModal(open);
  };

  const uniqueSignals = tree
    ? Array.from(new Set(tree.flatMap((node) => node.displayScalars?.map((scalar) => scalar.signal) ?? [])))
    : [];

  const selectOptionsSignal = uniqueSignals.map((signal) => ({
    value: signal,
    label: signal,
  }));

  const columnOptions: ColumnOrRowWithDefinitions[] = simpleColumns.filter((col: any) =>
    proportionByValidOptions.includes(col.title),
  );
  const selectOptionsColumn: SelectOption<any>[] = columnOptions
    ? columnOptions.map((col) => ({
        value: col.key,
        label: col.title ? t(col.title) : col.key,
      }))
    : [];

  const [serializedDisplayRange, setSerializedDisplayRange] = useState<SerializedRange>(serializeRange(displayRange));
  useEffect(() => setSerializedDisplayRange(serializeRange(displayRange)), [displayRange]);

  const onClickNode = (asset?: TreemapAsset, isLeafNode?: boolean) => {
    if (isLeafNode && asset) {
      handleSwapAssets(asset);
    } else {
      setTreemapViewSettings({ ...treemapViewSettings, parent: asset ?? treemapViewSettings.parent });
    }
  };

  const signalFound = selectOptionsSignal.some((opt) => opt.value === proportionBySignal);
  const columnFound = selectOptionsColumn.some((opt) => opt.value === proportionByColumn);

  const adjustedIsUniformSizing = (!(signalFound && columnFound) && !showModal) || isUniformSizing;

  return (
    <div className="height-maximum flex items-center justify-center">
      {!tree?.length ? (
        <LoadingFallback />
      ) : (
        <TreemapPropsOnly
          helpKey={helpKey}
          breadcrumbs={breadcrumbs}
          parent={parent}
          isContent={false}
          displayRange={serializedDisplayRange}
          timezone={timezone}
          rangeEditingEnabled={!isPresentationMode}
          onClickNode={onClickNode}
          tree={tree}
          isTableView={true}
          showModal={showModal}
          handleModalState={handleModalState}
          selectOptionsSignal={selectOptionsSignal}
          proportionBySignal={proportionBySignal}
          selectOptionsColumn={selectOptionsColumn}
          proportionByColumn={proportionByColumn}
          isUniformSizing={adjustedIsUniformSizing}
          treemapSquareScalingInverse={treemapSquareScalingInverse}
          saveTreemapConfiguration={saveTreemapConfiguration}
        />
      )}
    </div>
  );
};
